import type { BufferQueue, QueueItem } from './buffer-queue';
import type { NetworkAdapter } from './types';
import { DEFAULT_TRACK_ENDPOINT } from './constants';

export interface SchedulerOptions {
  queue: BufferQueue;
  network?: NetworkAdapter;
  endpoint?: string;
  batchSize?: number;
  flushIntervalMs?: number;
  baseRetryDelayMs?: number;
  maxRetries?: number;
  sendBatchHandler?: (items: QueueItem[]) => Promise<boolean | number>;
}

/**
 * 批量合并与指数退避重试调度器
 */
export class Scheduler {
  private queue: BufferQueue;
  private network?: NetworkAdapter;
  private endpoint: string;
  private batchSize: number;
  private flushIntervalMs: number;
  private baseRetryDelayMs: number;
  private maxRetries: number;
  private timer: any = null;
  private isFlushing = false;
  private sendBatchHandler?: (items: QueueItem[]) => Promise<boolean | number>;

  constructor(options: SchedulerOptions) {
    this.queue = options.queue;
    this.network = options.network;
    this.endpoint = options.endpoint ?? DEFAULT_TRACK_ENDPOINT;
    this.batchSize = options.batchSize ?? 10;
    this.flushIntervalMs = options.flushIntervalMs ?? 3000;
    this.baseRetryDelayMs = options.baseRetryDelayMs ?? 1000;
    this.maxRetries = options.maxRetries ?? 3;
    this.sendBatchHandler = options.sendBatchHandler;
  }

  public start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);
  }

  public stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  public async flush(): Promise<void> {
    if (this.isFlushing || this.queue.size() === 0) return;

    this.isFlushing = true;
    const items = this.queue.peek(this.batchSize);

    try {
      const result = await this.sendItems(items);
      if (result === true) {
        this.queue.remove(items);
      } else if (typeof result === 'number') {
        if (result > 0) this.queue.remove(items.slice(0, result));
        const remaining = items.slice(result);
        if (remaining.length > 0) this.handleFailure(remaining);
      } else {
        this.handleFailure(items);
      }
    } catch {
      this.handleFailure(items);
    } finally {
      this.isFlushing = false;
    }
  }

  private async sendItems(items: QueueItem[]): Promise<boolean | number> {
    if (this.sendBatchHandler) {
      return await this.sendBatchHandler(items);
    }

    if (!this.network) return false;

    // 默认通过网络适配器发送
    const payload = items.map((i) => i.data);
    const res = await this.network.post(this.endpoint, { events: payload });
    return res.status === 200 && (res.data?.code === '10000' || res.data?.code === 10000);
  }

  private handleFailure(items: QueueItem[]): void {
    for (const item of items) {
      item.retryCount = (item.retryCount ?? 0) + 1;
    }
    // 指数退避调度
    const maxAttempt = Math.max(...items.map((i) => i.retryCount ?? 0));
    if (maxAttempt > this.maxRetries) {
      // 超过重试上限，淘汰死信
      this.queue.remove(items);
      return;
    }
    const delay = this.baseRetryDelayMs * Math.pow(2, maxAttempt - 1);
    setTimeout(() => {
      this.flush();
    }, delay);
  }
}
